import React, { useState } from 'react';
import { Eye, EyeOff, Globe, Loader2, Sparkles, ArrowRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { useSalesAuth } from './SalesAuthContext';
import { useT } from './salesI18n';
import { apiError } from './salesApi';

const LOGO = `${process.env.PUBLIC_URL || ''}/sales-logo.png`;
const HERO = 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?crop=entropy&cs=srgb&fm=jpg&q=80&w=1400';

export default function SalesLogin() {
  const { login } = useSalesAuth();
  const { t, lang, setLang } = useT();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try { await login(username.trim(), password); } catch (err) { setError(err?.response?.status === 401 ? t.loginFailed : apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-5xl grid lg:grid-cols-[1.1fr_1fr] gap-6 items-stretch">
        <section className="relative hidden lg:flex flex-col justify-between rounded-[2rem] overflow-hidden sales-glass p-8 sales-rise">
          <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/75 via-slate-900/20 to-transparent" />
          <div className="relative z-10 flex items-center gap-4">
            <img src={LOGO} alt="PM Home Remodeling" className="h-20 w-auto object-contain drop-shadow-lg" data-testid="sales-login-logo" />
            <div className="text-white">
              <p className="font-bold text-lg leading-none">PM Home Remodeling</p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.25em] mt-1 text-white/80">{t.tagline}</p>
            </div>
          </div>
          <div className="relative z-10 text-white max-w-md">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/20 backdrop-blur px-3 py-1.5 text-xs font-semibold mb-5"><Sparkles className="h-3.5 w-3.5" /> Renewal by Andersen · Georgia</span>
            <h1 className="text-4xl xl:text-5xl font-extrabold leading-[1.05] tracking-tight">
              {lang === 'en' ? <>Measure once.<br />Close every time.</> : <>Meça uma vez.<br />Feche sempre.</>}
            </h1>
            <p className="mt-4 text-white/85 text-sm leading-relaxed">
              {lang === 'en' ? 'Leads, field visits, measurements, AI simulations and proposals — built for the road.' : 'Leads, visitas, medições, simulações por IA e propostas — feito para quem está na rua.'}
            </p>
          </div>
        </section>

        <section className="flex items-center justify-center">
          <div className="w-full max-w-md sales-rise sales-rise-1">
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-3 lg:hidden">
                <img src={LOGO} alt="PM" className="h-14 w-auto object-contain drop-shadow" data-testid="sales-login-logo-mobile" />
                <div>
                  <p className="font-bold leading-none text-slate-900">PM Home Remodeling</p>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.2em] mt-1 text-teal-700">{t.tagline}</p>
                </div>
              </div>
              <button type="button" data-testid="sales-login-lang-toggle" onClick={() => setLang(lang === 'en' ? 'pt-BR' : 'en')} className="ml-auto inline-flex items-center gap-1.5 rounded-full bg-white/70 border border-white/80 px-3 h-9 text-xs font-semibold text-slate-700 hover:bg-white transition-colors">
                <Globe className="h-3.5 w-3.5" /> {lang === 'en' ? 'EN' : 'PT'}
              </button>
            </div>

            <div className="sales-glass-strong rounded-[2rem] p-6 sm:p-8">
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t.loginTitle}</h2>
              <p className="text-sm text-slate-500 mt-1.5 mb-7">{t.loginSubtitle}</p>

              <form onSubmit={submit} className="space-y-4" data-testid="sales-login-form">
                <div>
                  <label className="text-xs font-semibold text-slate-600">{t.username}</label>
                  <Input data-testid="sales-login-username" value={username} onChange={(e) => setUsername(e.target.value)} className="h-12 mt-1.5" autoComplete="username" autoCapitalize="none" required />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600">{t.password}</label>
                  <div className="relative mt-1.5">
                    <Input data-testid="sales-login-password" type={show ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12 pr-11" autoComplete="current-password" required />
                    <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700" aria-label="toggle password" data-testid="sales-login-toggle-password">
                      {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {error && <p data-testid="sales-login-error" className="text-sm font-medium text-red-700 bg-red-50 border border-red-200 rounded-2xl px-3 py-2">{error}</p>}
                <button type="submit" data-testid="sales-login-submit-button" disabled={busy} className="w-full h-12 rounded-full bg-teal-600 hover:bg-teal-700 text-white font-semibold shadow-[0_8px_20px_-8px_rgba(13,148,136,0.7)] transition-all duration-200 hover:-translate-y-px active:scale-[0.98] flex items-center justify-center gap-2 disabled:opacity-60">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t.signIn} <ArrowRight className="h-4 w-4" /></>}
                </button>
              </form>
            </div>
            <p className="text-center text-[11px] text-slate-500 mt-4">© {new Date().getFullYear()} PM Home Remodeling</p>
          </div>
        </section>
      </div>
    </div>
  );
}
